import { useQuery } from 'react-query';

import { PageBoundingBox } from '../../../../backend/src/matter/fact/fact-bounding-box';
import { APIBaseChronos } from '@/api/hosts';
import { ApiError, QueryEntity } from '@/api/types';
import useGetFetchConfig from '@/api/useGetFetchConfig';

export interface KimThreadFileResponse {
  id: string;
  name: string;
  extension: string;
  pageDimensions: { width: number; height: number }[] | null;
  url: string;
  downloadUrl: string;
  chunk: {
    id: string;
    content: string;
    boundingBox: PageBoundingBox;
  } | null;
}

interface UseGetKimThreadFileArgs {
  matterId?: string;
  subsetId?: string;
  threadId?: string;
  chunkId?: string | null;
  fileId?: string | null;
}

// 409 is what the backend returns when the kim-agent has not yet
// written the converted PDF for a thread-file upload. Treat it as an
// expected lifecycle state rather than an error: surfaced as
// ``isProcessing`` so callers can render a "try again in a moment"
// signal instead of a generic error UI.
const STILL_PROCESSING_STATUS = 409;

export const useGetKimThreadFile = ({ matterId, subsetId, threadId, chunkId, fileId }: UseGetKimThreadFileArgs) => {
  const { fetchConfigGET } = useGetFetchConfig();

  const url =
    chunkId != null
      ? `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${threadId}/file-chunks/${chunkId}`
      : `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${threadId}/files/${fileId}`;

  // Normalise undefined → null in the query key so callers that pass
  // ``undefined`` for the unused id (the child component) share a cache
  // key with callers that pass ``null`` (the parent gate). Without this,
  // ``JSON.stringify`` drops undefined fields and the keys diverge,
  // forcing a second sequential fetch when the drawer mounts.
  const query = useQuery<KimThreadFileResponse, ApiError>(
    [QueryEntity.KimThreadFile, { chunkId: chunkId ?? null, fileId: fileId ?? null }],
    async () => {
      const res = await fetch(url, fetchConfigGET);
      if (!res.ok) {
        const error = await res.json();
        throw new ApiError(res.status, error.message);
      }
      return res.json();
    },
    {
      enabled: !!matterId && !!subsetId && !!threadId && (!!chunkId || !!fileId),
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => error.code !== 404 && error.code !== STILL_PROCESSING_STATUS && failureCount < 3,
    },
  );

  return { ...query, isProcessing: query.error?.code === STILL_PROCESSING_STATUS };
};
